import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Undo2, AlertTriangle } from "lucide-react";
import { fetchReport, recordTrip, recordAllTrips, recordShovelTrips, undoTrip } from "@/api";

export default function TripGrid({ activeShift }) {
  const [report, setReport] = useState(null);
  const [flash, setFlash] = useState(null);
  const timers = useRef({});

  const shiftId = activeShift?.id;

  const load = useCallback(async () => {
    if (!shiftId) return;
    try {
      setReport(await fetchReport(shiftId));
    } catch (e) {
      console.error(e);
    }
  }, [shiftId]);

  useEffect(() => {
    setReport(null);
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shiftId]);

  const tap = async (d) => {
    setFlash(d.dumper_id);
    clearTimeout(timers.current[d.dumper_id]);
    timers.current[d.dumper_id] = setTimeout(() => setFlash(null), 150);
    setReport((r) =>
      r
        ? {
            ...r,
            total_trips: r.total_trips + 1,
            coal_trips: r.coal_trips + (d.material === "COAL" ? 1 : 0),
            ob_trips: r.ob_trips + (d.material === "OB" ? 1 : 0),
            per_dumper: r.per_dumper.map((x) =>
              x.dumper_id === d.dumper_id ? { ...x, trips: x.trips + 1 } : x
            ),
          }
        : r
    );
    try {
      await recordTrip(d.dumper_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to record trip");
      load();
    }
  };

  const undo = async (e, d) => {
    e.stopPropagation();
    try {
      await undoTrip(d.dumper_id);
      toast.success(`Undo: 1 trip removed from ${d.dumper_number}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Nothing to undo");
    }
  };

  const countAll = async () => {
    if (!window.confirm("Record +1 trip for ALL allotted dumpers?")) return;
    try {
      const res = await recordAllTrips();
      toast.success(`Recorded +1 trip for ${res.count} dumpers`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to record trips");
    }
  };

  const countShovel = async (shovelNumber) => {
    const s = report?.per_shovel?.find((x) => x.shovel_number === shovelNumber);
    if (!s) return;
    if (!window.confirm(`Record +1 trip for ALL dumpers of Shovel ${shovelNumber}?`)) return;
    try {
      const res = await recordShovelTrips(s.shovel_id);
      toast.success(`Recorded +1 trip for ${res.count} dumpers of Shovel ${shovelNumber}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to record trips");
    }
  };

  if (!activeShift) {
    return (
      <div className="border-2 border-black bg-zinc-100 p-10 text-center hard-shadow" data-testid="no-shift-empty">
        <AlertTriangle size={40} strokeWidth={2.5} className="mx-auto mb-3" />
        <p className="font-head text-xl font-bold uppercase">No active shift</p>
        <p className="text-sm font-medium text-zinc-500 mt-1">
          Ask the Shift Manager to start a shift and allot dumpers.
        </p>
      </div>
    );
  }

  const dumpers = report?.per_dumper || [];

  return (
    <div data-testid="tripman-screen">
      <div className="flex flex-wrap gap-4 mb-6" data-testid="live-totals">
        <div className="border-2 border-black bg-black text-white px-5 py-3 hard-shadow">
          <div className="text-[10px] font-bold uppercase tracking-widest text-[#FFB800]">Total Trips</div>
          <div className="font-num text-5xl font-bold leading-none" data-testid="total-trips-count">
            {report?.total_trips ?? 0}
          </div>
        </div>
        <div className="border-2 border-black bg-white px-5 py-3 hard-shadow">
          <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Coal</div>
          <div className="font-num text-5xl font-bold leading-none" data-testid="coal-trips-count">
            {report?.coal_trips ?? 0}
          </div>
        </div>
        <div className="border-2 border-black bg-white px-5 py-3 hard-shadow">
          <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">OB</div>
          <div className="font-num text-5xl font-bold leading-none" data-testid="ob-trips-count">
            {report?.ob_trips ?? 0}
          </div>
        </div>
      </div>

      {dumpers.length > 0 && (
        <button
          data-testid="count-all-btn"
          onClick={countAll}
          className="w-full min-h-[64px] border-2 border-black bg-black text-[#FFB800] hard-shadow pressable font-head text-xl font-black uppercase mb-6"
        >
          +1 Trip — All Allotted Dumpers
        </button>
      )}

      {dumpers.length === 0 ? (
        <div className="border-2 border-black bg-zinc-100 p-10 text-center hard-shadow" data-testid="no-dumpers-empty">
          <p className="font-head text-xl font-bold uppercase">No dumpers allotted yet</p>
          <p className="text-sm font-medium text-zinc-500 mt-1">
            Shift Manager must assign dumpers to shovels in the Shift Manager tab.
          </p>
        </div>
      ) : (
        <div data-testid="dumper-grid">
          {Object.entries(
            dumpers.reduce((g, d) => {
              (g[d.shovel_number] = g[d.shovel_number] || []).push(d);
              return g;
            }, {})
          ).map(([shovelNumber, group]) => (
          <div key={shovelNumber} className="mb-6">
            <div
              className="flex items-center justify-between gap-2 border-2 border-black bg-zinc-100 px-3 py-2 mb-3 hard-shadow-sm flex-wrap"
              data-testid={`shovel-group-${shovelNumber}`}
            >
              <span className="font-head text-lg font-black uppercase">
                Shovel {shovelNumber}
                {report?.per_shovel?.find((s) => s.shovel_number === shovelNumber)?.shovel_operator && (
                  <span className="text-xs font-semibold text-zinc-500 ml-2 normal-case">
                    {report.per_shovel.find((s) => s.shovel_number === shovelNumber).shovel_operator}
                  </span>
                )}
              </span>
              <button
                data-testid={`shovel-count-all-${shovelNumber}`}
                onClick={() => countShovel(shovelNumber)}
                className="min-h-[48px] px-4 border-2 border-black bg-[#FFB800] hard-shadow-sm pressable font-head text-sm font-black uppercase"
              >
                +1 Trip — All Dumpers of This Shovel
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {group.map((d) => (
            <div
              key={d.dumper_id}
              data-testid={`dumper-tap-card-${d.dumper_number}`}
              onClick={() => tap(d)}
              className={`border-2 border-black hard-shadow pressable cursor-pointer select-none bg-white ${
                flash === d.dumper_id ? "tap-flash" : ""
              }`}
            >
              <div className="flex items-center justify-between gap-2 border-b-2 border-black px-3 py-2">
                <span className="font-head text-2xl font-black tracking-tight min-w-0 truncate">{d.dumper_number}</span>
                <span
                  className={`text-[10px] font-bold uppercase px-2 py-0.5 border-2 border-black ${
                    d.material === "COAL" ? "bg-black text-white" : "bg-[#FFB800] text-black"
                  }`}
                >
                  {d.material}
                </span>
              </div>
              <div className="px-3 pt-2 pb-1">
                <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                  Shovel {d.shovel_number}{d.dumper_operator ? ` • ${d.dumper_operator}` : ""}
                </div>
                <div
                  className="font-num text-7xl sm:text-8xl font-bold leading-none tracking-tighter"
                  data-testid={`trip-count-${d.dumper_number}`}
                >
                  {d.trips}
                </div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 h-4" data-testid={`last-trip-time-${d.dumper_number}`}>
                  {d.last_trip_at ? `Last: ${new Date(d.last_trip_at).toLocaleTimeString()}` : ""}
                </div>
              </div>
              <div className="px-3 pb-3 flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">Tap = +1 trip</span>
                <button
                  data-testid={`undo-trip-${d.dumper_number}`}
                  onClick={(e) => undo(e, d)}
                  className="flex items-center gap-1 border-2 border-black bg-white px-2 py-1.5 text-xs font-bold uppercase pressable hard-shadow-sm hover:bg-zinc-100"
                >
                  <Undo2 size={14} strokeWidth={3} /> Undo
                </button>
              </div>
            </div>
              ))}
            </div>
          </div>
          ))}
        </div>
      )}
    </div>
  );
}
